/************************************************************************************
 * [MODULE]: PRODUCT INFORMATION
 * Main components:
 * - l-product-form: Handles form submission and cart interactions
 * - l-variant-selects: Manages variant selection and option changes
 * - l-product-controls: Controls product quantity and variant-specific logic
 * - l-product-quantity-input: Handles quantity input interactions
 *
 * Internal Events:
 * - l:variant:changed: Fired when the variant changes
 * - l:quantity:changed: Fired when the quantity changes
 * - l:product:unavailable: Fired when the product is unavailable
 * - l:variant:option-changed: Fired when the variant option changes
 * - l:sellingPlan:changed: Fired when the selling plan changes
 * - l:addon-products:changed: Fired when the addon products change
 * External Events:
 * - l:cart:closed: Fired when the cart is closed
 *************************************************************************************/

if (!customElements.get('l-product-info')) {
	customElements.define(
		'l-product-info',
		class LProductInfo extends HTMLElement {
			quantityInput = undefined
			quantityForm = undefined
			abortController = undefined
			pendingRequestUrl = null
			preProcessHtmlCallbacks = []
			postProcessHtmlCallbacks = []

			constructor() {
				super()
			}

			connectedCallback() {
				// Elements
				this.productFormEl = this.querySelector('l-product-form')
				this.initializeProductSwapUtility()
				this.selectedSellingPlanId = this.dataset.sellingPlanId
				this.productId = this.dataset.productId
				this.dispatchEvent(new CustomEvent('l:product-info:loaded', { bubbles: true }))
				this.setupEventListeners()
			}

			setupEventListeners() {
				// Variant option changes
				document.addEventListener('l:variant:option-changed', (event) => {
					const { target, selectedOptionValues, productId, sectionId } = event.detail
					if (!this.contains(target) || productId !== this.productId || sectionId !== this.sectionId) return
					this.handleOptionValueChange(target, selectedOptionValues, null)
				})

				// Selling plan changes
				document.addEventListener('l:sellingPlan:changed', (event) => {
					const { sellingPlanId, productId, sectionId, target } = event.detail
					if (!this.contains(target) || productId !== this.productId || sectionId !== this.sectionId) return
					const variant = this.getSelectedVariant(this)
					this.selectedSellingPlanId = sellingPlanId
					this.handleOptionValueChange(event.target, null, variant)
				})

				// Product Added: This is used to update the product info when a product is added to the cart
				document.addEventListener('l:product:added', (event) => {
					const { id, response: addedVariant } = event.detail
					const currentVariant = this.getSelectedVariant(this)
					const shouldUpdate = addedVariant?.id == currentVariant?.id
					if (shouldUpdate) {
						this.handleOptionValueChange(null, null, currentVariant)
					}
				})

				// Cart updates
				document.addEventListener('l:cart:closed', (event) => {
					const { updatedVariants } = event.detail
					const variant = this.getSelectedVariant(this)
					const shouldUpdate = updatedVariants.includes(`${variant?.id}`)
					if (shouldUpdate) this.handleOptionValueChange(null, null, variant)
				})
			}

			addPreProcessCallback(callback) {
				this.preProcessHtmlCallbacks.push(callback)
			}

			initializeProductSwapUtility() {
				this.postProcessHtmlCallbacks.push((newNode) => {
					// Re-initialize Shopify functionality after DOM updates
					window?.Shopify?.PaymentButton?.init()
					window?.ProductModel?.loadShopifyXR()
				})
			}

			handleOptionValueChange(target, selectedOptionValues, variant) {
				// Reset form state
				this.resetProductFormState()
				const productUrl = target?.dataset?.productUrl || this.pendingRequestUrl || this.dataset.url
				this.pendingRequestUrl = productUrl
				const shouldSwapProduct = this.dataset.url !== productUrl
				const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct
				this.renderProductInfo({
					requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, variant, shouldFetchFullPage),
					targetId: target?.id,
					callback: shouldSwapProduct
						? this.handleSwapProduct(productUrl, shouldFetchFullPage)
						: this.handleUpdateProductInfo(productUrl),
				})
			}

			resetProductFormState() {
				if (this.productFormEl) {
					this.productFormEl.global_toggleSubmitButton(true, null, false)
					this.productFormEl.handleErrorMessage()
				}
			}

			renderProductInfo({ requestUrl, targetId, callback }) {
				// Cancel any pending requests
				this.abortController?.abort()
				this.abortController = new AbortController()
				fetch(requestUrl, { signal: this.abortController.signal })
					.then((response) => response.text())
					.then((responseText) => {
						this.pendingRequestUrl = null
						const html = new DOMParser().parseFromString(responseText, 'text/html')
						callback(html)
					})
					.then(() => {
						// Focus the element that triggered the update
						document.querySelector(`#${targetId}`)?.focus()
					})
					.catch((error) => {
						// Only log actual errors, not aborted requests
						if (error.name !== 'AbortError') {
							console.error('Product info fetch error:', error)
						}
					})
			}

			handleSwapProduct(productUrl, updateFullPage) {
				return (html) => {
					const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info'
					const variant = this.getSelectedVariant(html.querySelector(selector))
					// Update URL with new variant ID
					this.updateURL(productUrl, variant?.id)
					if (updateFullPage) {
						// Update page title
						document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML
						// Swap the entire main content
						l_swapHtmlNode(
							document.querySelector('main'),
							html.querySelector('main'),
							this.preProcessHtmlCallbacks,
							this.postProcessHtmlCallbacks
						)
					} else {
						// Swap just the product info section
						l_swapHtmlNode(
							this,
							html.querySelector('product-info'),
							this.preProcessHtmlCallbacks,
							this.postProcessHtmlCallbacks
						)
					}
				}
			}

			handleUpdateProductInfo(productUrl) {
				return (html) => {
					const variant = this.getSelectedVariant(html)
					// Update DOM elements
					this.updateOptionValues(html)
					this.updateURL(productUrl, variant?.id)
					this.updateVariantInputs(variant?.id)
					// Handle unavailable variant
					if (!variant) {
						document.dispatchEvent(
							new CustomEvent('l:variant:changed', {
								detail: { sectionId: this.sectionId, html, productId: this.dataset.productId, variant },
							})
						)
						return
					}
					// Update product sections from HTML response
					this.updateProductSections(html)
                    this.querySelectorAll('[data-baja-formula-content]').forEach((destination) => {
                        const source = html.querySelector(`[data-baja-formula-content="${destination.dataset.bajaFormulaContent}"]`)
                        if (source) destination.innerHTML = source.innerHTML
                    })
					// Notify about variant change
					document.dispatchEvent(
						new CustomEvent('l:variant:changed', {
							detail: { sectionId: this.sectionId, html, productId: this.dataset.productId, variant },
						})
					)
				}
			}

			updateProductSections(html) {
				const updateSourceFromDestination = (id, htmlMethod = 'innerHTML', shouldHide = () => false) => {
					const source = html.getElementById(`${id}-${this.sectionId}`)
					const destination = document.getElementById(`${id}-${this.dataset.section}`)
					if (source && destination) {
						destination[htmlMethod] = source[htmlMethod]
						destination.classList.toggle('l-hidden', shouldHide(source))
					}
				}
				// Update product sections
				updateSourceFromDestination('csStock', 'outerHTML')
                updateSourceFromDestination('stockInfo', 'outerHTML')
				updateSourceFromDestination('stickyATC', 'outerHTML')
				updateSourceFromDestination('stockInformation', 'outerHTML')
			}

			updateOptionValues(html) {
				const variantSelects = html.querySelector('l-variant-selects')
				if (variantSelects) {
					l_swapHtmlNode(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks)
				}
			}

			updateURL(url, variantId) {
				if (this.dataset.updateUrl === 'false') return

				const currentUrlParams = new URLSearchParams(window.location.search)
				const params = new URLSearchParams()
                if (currentUrlParams.has('preview_theme_id')) params.set('preview_theme_id', currentUrlParams.get('preview_theme_id'))

				if (variantId) params.append('variant', variantId)

				if (this.selectedSellingPlanId) params.append('selling_plan', this.selectedSellingPlanId)

				const viewTemplate = currentUrlParams.get('view')
				if (viewTemplate) params.append('view', viewTemplate)

				const newUrl = `${url}${params.size > 0 ? '?' : ''}${params.toString()}`

				window.history.replaceState({}, '', newUrl)
			}

			updateVariantInputs(variantId) {
				this.querySelectorAll(`#product-form-${this.dataset.section}`).forEach((productForm) => {
					const input = productForm.querySelector('input[name="id"]')
					input.value = variantId ?? ''
					input.dispatchEvent(new Event('change', { bubbles: true }))
				})
			}

			getSelectedVariant(productInfoNode) {
				const selectedVariant = productInfoNode.querySelector(
					'l-variant-selects [data-selected-variant], [data-selected-variant]'
				)?.innerHTML

				return selectedVariant ? JSON.parse(selectedVariant) : null
			}

			buildRequestUrlWithParams(url, optionValues, variant, shouldFetchFullPage = false) {
				const params = []
				// Add section ID if not fetching full page
				if (!shouldFetchFullPage) {
					params.push(`section_id=${this.sectionId}`)
				}
				// Add selling plan if selected
				if (this.selectedSellingPlanId) {
					params.push(`selling_plan=${this.selectedSellingPlanId}`)
				}
				// Add option values or variant ID
				if (optionValues?.length) {
					params.push(`option_values=${optionValues.join(',')}`)
				} else if (variant) {
					params.push(`variant=${variant.id}`)
				}
				return `${url}?${params.join('&')}`
			}

			get productForm() {
				return this.querySelector('l-product-form')
			}

			get variantSelectors() {
				return this.querySelector('l-variant-selects')
			}

			get sectionId() {
				return this.dataset.originalSection || this.dataset.section
			}
		}
	)
}

if (!customElements.get('l-product-form')) {
	customElements.define(
		'l-product-form',
		class LTAProductForm extends HTMLElement {
			constructor() {
				super()
				this._boundHandleOnQuantityBreaksChanged = this.handleOnQuantityBreaksChanged.bind(this)
			}

			connectedCallback() {
				// Cache DOM elements
				this.form = this.querySelector('form') || document.getElementById(this.dataset.formId)
				this.cart = document.getElementById('CartDrawer')
				this.submitButtonEl = this.querySelector('[type="submit"]')
				this.submitButtonTextEl = this.submitButtonEl.querySelector('[data-add-to-cart-label]')
				this.dynamicCheckoutEl = this.querySelector('[data-dynamic-checkout]')
				this.backInStockButtonEl = this.querySelector('[data-open-back-in-stock-trigger]')

				// Store section data
				this.sectionId = this.dataset.sectionId
				this.productId = this.dataset.productId
				this.selectedVariantId = this.dataset.selectedVariantId
				this.hideErrors = this.dataset.hideErrors === 'true'

				// Initialize event listeners
				if (this.form) {
					this.form.addEventListener('submit', this.onSubmitHandler.bind(this))
				}

				document.addEventListener('l:variant:changed', this.handleOnVariantChange.bind(this))
				document.addEventListener('l:quantity-breaks:changed', this._boundHandleOnQuantityBreaksChanged)
			}

			disconnectedCallback() {
				document.removeEventListener('l:variant:changed', this.handleOnVariantChange)
				document.removeEventListener('l:quantity-breaks:changed', this._boundHandleOnQuantityBreaksChanged)
			}

			handleOnQuantityBreaksChanged(event) {
				const { sectionId, productId, price } = event.detail || {}

				if (productId !== this.productId || sectionId !== this.sectionId) return

				this.quantityBreaksPrice = price

				const newPriceEl = this.querySelector('[data-product-normal-price]')
				if (newPriceEl) {
					newPriceEl.textContent = this.quantityBreaksPrice
				}
			}

			handleOnVariantChange(event) {
				const { sectionId, html, productId, variant } = event.detail

				if (productId !== this.productId || sectionId !== this.sectionId) return

				// Update back in stock button
				if (this.backInStockButtonEl) {
					this.backInStockButtonEl.classList.toggle('l-hidden', !variant || (variant && variant.available))
				}

				// Hide items if variant is not available
				if (this.dynamicCheckoutEl) {
					this.dynamicCheckoutEl.classList.toggle('l-hidden', !variant)
				}

				// Hide submit button if variant is not exists
				if (!variant) {
					this.global_toggleSubmitButton(true, 'unavailable')

					return
				}

				// Replace submit button
				const newSubmitButtonEl = html.getElementById(`ProductSubmitButton-${this.sectionId}`)

				if (!newSubmitButtonEl) return
				const newPriceEl = newSubmitButtonEl.querySelector('[data-product-normal-price]')
				if (newPriceEl) {
					newPriceEl.textContent = this.quantityBreaksPrice
				}

				this.submitButtonEl.replaceWith(newSubmitButtonEl)

				// Re-query elements
				this.submitButtonEl = this.querySelector('[type="submit"]')
				this.submitButtonTextEl = this.submitButtonEl.querySelector('[data-add-to-cart-label]')
			}

			onSubmitHandler(evt) {
				evt.preventDefault()

				// Don't submit if button is disabled
				if (this.submitButtonEl.getAttribute('aria-disabled') === 'true') return

				// Set button to loading state
				this.submitButtonEl.setAttribute('aria-disabled', true)
				this.submitButtonEl.classList.add('is-loading')

				// Create cart configuration and add to cart
				const config = ltaCartService.createConfigFromForm(this.form)

				ltaCartService
					.addToCart(config, 'l-product-form', {
						button: this.submitButtonEl,
						labelOptions: {
							getDefaultLabel: () => this.getButtonLabel('addToCart'),
						},
					})
					.then((response) => {
						if (response?.status) {
							this.handleErrorMessage(response.description)
						}
					})
					.catch((e) => {
						console.error('Add to cart error:', e)
					})
					.finally(() => {
						// Remove loading state
						this.submitButtonEl.classList.remove('is-loading')

						if (!this.error) this.submitButtonEl.removeAttribute('aria-disabled')

						l_debounce(() => this.handleErrorMessage.bind(this, false), 1000)()
					})
			}

			handleErrorMessage(errorMessage = false) {
				if (this.hideErrors) return

				// Find error message elements if not already cached
				if (!this.errorMessageWrapper) {
					this.errorMessageWrapper = document.getElementById(`ProductErrorMessages-${this.sectionId}`)
					this.errorMessage = this.errorMessageWrapper?.querySelector('[data-error-message]')
				}

				if (!this.errorMessageWrapper) return

				// Toggle error message visibility
				this.errorMessageWrapper.classList.toggle('l-hidden', !errorMessage)
				if (errorMessage) this.errorMessage.textContent = errorMessage
			}

			global_toggleSubmitButton(disable = true, textType, needToUpdateText = true) {
				if (disable) {
					this.submitButtonEl.setAttribute('disabled', 'disabled')
					if (textType && needToUpdateText) this.submitButtonTextEl.textContent = this.getButtonLabel(textType)

					return
				}

				this.submitButtonEl.removeAttribute('disabled')

				if (needToUpdateText) {
					this.submitButtonTextEl.textContent = this.getButtonLabel(textType)
				}
			}

			getButtonLabel(textType = 'addToCart') {
				switch (textType) {
					case 'addToCart':
						return this.dataset.atcLabel || window.LTATheme.variantStrings.addToCart
					case 'soldOut':
						return this.dataset.soldOutLabel || window.LTATheme.variantStrings.soldOut
					case 'preOrder':
						return this.dataset.preOrderLabel || window.LTATheme.variantStrings.preOrder
					case 'unavailable':
						return this.dataset.unavailableLabel || window.LTATheme.variantStrings.unavailable
					default:
						return window.LTATheme.variantStrings.addToCart
				}
			}

			get variantIdInput() {
				return this.form.querySelector('[name=id]')
			}
		}
	)
}

if (!customElements.get('l-variant-selects')) {
	class LTAVariantSelect extends HTMLElement {
		constructor() {
			super()
			this.onChange = this.onChange.bind(this)
		}

		connectedCallback() {
			this.productId = this.dataset.productId
			this.sectionId = this.dataset.sectionId
			this.addEventListener('change', this.onChange)
		}

		onChange(event) {
			const target = this._getInputFromEvent(event.target)
			this._updateSelectionMetadata(target)

			document.dispatchEvent(
				new CustomEvent('l:variant:option-changed', {
					detail: {
						event,
						target,
						selectedOptionValues: this.selectedOptionValues,
						productId: this.productId,
						sectionId: this.sectionId,
					},
				})
			)
		}

		_updateSelectionMetadata(target) {
			const { value, tagName } = target

			// Skip for select elements with selected options
			if (tagName === 'SELECT' && target.selectedOptions.length) return

			// Update selected value display for swatch inputs
			if (target.hasAttribute('data-product-variant-input')) {
				const optionsContainer = target.closest('[data-variant-options]')
				if (!optionsContainer) return
				const selectedSwatchValue = optionsContainer.querySelector('[data-selected-value]')
				if (selectedSwatchValue) {
					selectedSwatchValue.innerHTML = value
				}
			}
		}

		_getInputFromEvent(target) {
			return target.tagName === 'SELECT' ? target.selectedOptions[0] : target
		}

		get selectedOptionValues() {
			return Array.from(
				this.querySelectorAll('[data-variant-select] option[selected], [data-product-variant-input]:checked')
			).map((el) => el.dataset.optionValueId)
		}
	}

	customElements.define('l-variant-selects', LTAVariantSelect)
}

if (!customElements.get('l-product-quantity-input')) {
	class LTAProductQuantityInput extends customElements.get('l-qty-input') {
		constructor() {
			super()
			this.onVariantChanged = this.updateQuantityRules.bind(this)
		}

		connectedCallback() {
			super.connectedCallback()

			if (!this.input) return

			this.productId = this.dataset.productId
			this.sectionId = this.dataset.sectionId

			document.addEventListener('l:variant:changed', this.onVariantChanged)
		}

		onQuantityChanged(oldValue, newValue) {
			this.input.dispatchEvent(
				new CustomEvent('l:quantity:changed', {
					bubbles: true,
					detail: {
						quantity: newValue,
						productId: this.productId,
						sectionId: this.sectionId,
					},
				})
			)
		}

		updateQuantityRules(event) {
			const { html, sectionId, productId, variant } = event.detail

			if (productId !== this.productId || sectionId !== this.sectionId || !variant) return

			const quantityFormUpdated = html.getElementById(`Product-Form-Controls-${sectionId}`)
			const newInputEl = quantityFormUpdated?.querySelector('[data-qty-input]')

			this.input.value = newInputEl.value
			this.input.setAttribute('min', newInputEl.min)
			this.input.setAttribute('max', newInputEl.max)
			this.input.setAttribute('step', newInputEl.step)
			this.input.toggleAttribute('disabled', newInputEl.disabled)

			this.input.dispatchEvent(this.changeEvent)

			this.validateQtyRules() // Validate quantity rules
		}

		disconnectedCallback() {
			document.removeEventListener('l:variant:changed', this.onVariantChanged)
			super.disconnectedCallback()
		}
	}

	customElements.define('l-product-quantity-input', LTAProductQuantityInput)
}

// Note: placed this component at the end of the file to avoid race conditions
if (!customElements.get('l-product-price')) {
	class LTAProductPrice extends HTMLElement {
		constructor() {
			super()
			this.addOnPrice = {}

			this.handleOnQuantityChange = this.handleOnQuantityChange.bind(this)
			this.handleOnVariantChange = this.handleOnVariantChange.bind(this)
			this.handleOnAddOnChange = this.handleOnAddOnChange.bind(this)
		}

		connectedCallback() {
			this.sectionId = this.dataset.sectionId
			this.productId = this.dataset.productId

			this._cacheElements()
			this._updateJsonData(this)

			document.addEventListener('l:quantity:changed', this.handleOnQuantityChange)
			document.addEventListener('l:variant:changed', this.handleOnVariantChange)
			document.addEventListener('l:addon-products:changed', this.handleOnAddOnChange)
		}

		disconnectedCallback() {
			document.removeEventListener('l:quantity:changed', this.handleOnQuantityChange)
			document.removeEventListener('l:variant:changed', this.handleOnVariantChange)
			document.removeEventListener('l:addon-products:changed', this.handleOnAddOnChange)
		}

		_cacheElements() {
			this.priceEl = this.querySelector('[data-price]')
			this.compareAtPriceEl = this.querySelector('[data-compare-at-price]')
			this.saleTagEl = this.querySelector('[data-sale-tag]')
		}

		handleOnQuantityChange(event) {
			const { sectionId, productId, quantity } = event.detail

			if (productId !== this.productId || sectionId !== this.sectionId) return

			this.priceData = {
				...this.priceData,
				quantity,
			}

			this._updatePrice()
		}

		handleOnVariantChange(event) {
			const { sectionId, html, productId, variant } = event.detail
			if (productId !== this.productId || sectionId !== this.sectionId) return

			this.classList.toggle('l-hidden', !variant)
			if (!variant) return

			const newPriceEl = html.getElementById(`price-${sectionId}`)
			if (newPriceEl) this._updateJsonData(newPriceEl)

			this._updatePrice()
		}

		handleOnAddOnChange(event) {
			const { id, totalNormalPrice, totalComparePrice, productId, sectionId } = event.detail
			if (productId !== this.productId || sectionId !== this.sectionId) return

			this.addOnPrice[id] = { totalNormalPrice, totalComparePrice }
			this._updatePrice()
		}

		_updateJsonData(priceEl) {
			const script = priceEl.querySelector('script[type="application/json"]')
			if (!script) {
				this.priceData = {}
				return
			}

			let newPriceData = JSON.parse(script.textContent)

			const minQty = Number(newPriceData.min ?? 1)
			newPriceData = {
				...newPriceData,
				quantity: minQty,
			}

			this.priceData = newPriceData
		}

		_updatePrice() {
			const {
				comparePrice,
				normalPrice,
				newNormalPriceFormatted,
				newComparePriceFormatted,
				salePercentage,
				salePriceFormatted,
				saleTagLabel,
			} = this._calculatePrices()

			if (this.priceEl) this.priceEl.innerHTML = newNormalPriceFormatted

			const hideCompare = !comparePrice || comparePrice === 0 || comparePrice <= normalPrice
			if (this.compareAtPriceEl) {
				this.compareAtPriceEl.classList.toggle('l-hidden', hideCompare)
				if (!hideCompare) {
					this.compareAtPriceEl.innerHTML = newComparePriceFormatted
				}
			}

			if (this.saleTagEl && saleTagLabel) {
				this.saleTagEl.classList.toggle('l-hidden', !comparePrice || comparePrice <= normalPrice)

				this.saleTagEl.innerHTML = saleTagLabel
					.replace('[percentage]', `${salePercentage}%`)
					.replace('[amount]', salePriceFormatted)
			}

			const globalNormalPriceEls = document.querySelectorAll(`[data-product-normal-price="${this.sectionId}"]`)
			globalNormalPriceEls.forEach((el) => {
				el.innerHTML = newNormalPriceFormatted
			})
		}

		_calculatePrices() {
			let {
				normalPrice = 0,
				comparePrice = 0,
				saleTagLabel = '',
				quantity = 1,
				quantity_price_breaks = [],
			} = this.priceData

			if (Array.isArray(quantity_price_breaks) && quantity_price_breaks.length > 0) {
				const priceBreak = quantity_price_breaks
					.slice()
					.sort((a, b) => a.minimumQuantity - b.minimumQuantity)
					.reduce((acc, curr) => (quantity >= curr.minimumQuantity ? curr : acc), null)

				if (priceBreak) normalPrice = priceBreak.money
			}

			const totalAddOnNormalPrice = this._getTotalAddOn('totalNormalPrice')
			const totalAddOnComparePrice = this._getTotalAddOn('totalComparePrice')

			const newNormalPrice = normalPrice * quantity + totalAddOnNormalPrice
			const newComparePrice = comparePrice * quantity + totalAddOnComparePrice

			const newNormalPriceFormatted = l_formatMoney(newNormalPrice)
			const newComparePriceFormatted = l_formatMoney(newComparePrice)

			const salePercentage =
				newComparePrice > 0 ? parseFloat(((newComparePrice - newNormalPrice) / newComparePrice) * 100).toFixed(0) : 0
			const salePriceFormatted = l_formatMoney(newComparePrice - newNormalPrice)

			return {
				normalPrice,
				comparePrice,
				salePercentage,
				newNormalPriceFormatted,
				newComparePriceFormatted,
				salePriceFormatted,
				saleTagLabel,
			}
		}

		_getTotalAddOn(key) {
			return Object.values(this.addOnPrice).reduce((acc, curr) => acc + (curr[key] || 0), 0)
		}
	}

	customElements.define('l-product-price', LTAProductPrice)
}

if (!customElements.get('l-product-installment-payments')) {
	class LTAProductInstallmentPayments extends HTMLElement {
		constructor() {
			super()
			this._boundOnVariantChange = this._onVariantChange.bind(this)
		}

		connectedCallback() {
			this.sectionId = this.dataset.sectionId
			this.productId = this.dataset.productId

			this._applyInstallmentsFont()

			document.addEventListener('l:variant:changed', this._boundOnVariantChange)
		}

		disconnectedCallback() {
			document.removeEventListener('l:variant:changed', this._boundOnVariantChange)
			this._installmentsObserver?.disconnect()
		}

		_applyInstallmentsFont() {
			const inject = (el, retries = 10) => {
				if (!el.shadowRoot) {
					if (retries > 0) requestAnimationFrame(() => inject(el, retries - 1))
					return
				}
				if (el.shadowRoot.getElementById('l-installments-font')) return
				const style = document.createElement('style')
				style.id = 'l-installments-font'
				style.textContent = `* { font-family: inherit !important; }`
				el.shadowRoot.appendChild(style)
			}

			const el = this.querySelector('shopify-payment-terms')
			if (el) return inject(el)

			this._installmentsObserver = new MutationObserver(() => {
				const target = this.querySelector('shopify-payment-terms')

				if (target) {
					inject(target)
					this._installmentsObserver.disconnect()
				}
			})
			this._installmentsObserver.observe(this, { childList: true, subtree: true })
		}

		_onVariantChange(event) {
			const { sectionId, productId, variant } = event.detail || {}
			if (productId !== this.productId || sectionId !== this.sectionId || !variant) return

			const inputs = this.querySelectorAll('input[name="id"]')
			inputs.forEach((input) => {
				input.value = variant.id
			})
		}
	}

	customElements.define('l-product-installment-payments', LTAProductInstallmentPayments)
}

/************************************************************************************
 *
 * [MODULE]: PRODUCT MEDIA
 *
 *************************************************************************************/

if (!customElements.get('l-product-media-gallery')) {
	class ProductMediaGallery extends HTMLElement {
		constructor() {
			super()
			this.sectionId = this.dataset.sectionId
			this.productId = this.dataset.productId
			this._boundSetupCarousel = this.setupCarousel.bind(this)
			this._boundOnVariantChange = this.onVariantChange.bind(this)
			this._boundOnWindowResize = this._onWindowResize.bind(this)
			this._resizeRaf = null
			this._mainCarouselResizeObserver = null
			this._mainMediaHeightRaf = null
			this._activeGalleryRoot = null
			this._slideChangeHandler = null
			this._slideChangeTransitionEndHandler = null
		}

		connectedCallback() {
			this.productId = this.dataset.productId
			this.sectionId = this.dataset.sectionId

			this._initModalZoomEvents()

			window.addEventListener('resize', this._boundOnWindowResize)

			document.addEventListener('l:vendor:loaded', this._boundSetupCarousel)
			document.addEventListener('l:variant:changed', this._boundOnVariantChange)
		}

		disconnectedCallback() {
			document.removeEventListener('l:vendor:loaded', this._boundSetupCarousel)
			document.removeEventListener('l:variant:changed', this._boundOnVariantChange)
			window.removeEventListener('resize', this._boundOnWindowResize)
			if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf)
			if (this._mainMediaHeightRaf) cancelAnimationFrame(this._mainMediaHeightRaf)
			this._destroyCarousels()
			this._mainCarouselResizeObserver?.disconnect()
			this._mainCarouselResizeObserver = null
		}

		_getGalleryRootForVariant(variant) {
			if (!variant?.id) return this.querySelector('[data-default-media-gallery]')

			const variantGallery = this.querySelector(`[data-variant-media-gallery][data-variant-id="${variant.id}"]`)

			return variantGallery || this.querySelector('[data-default-media-gallery]')
		}

		_getInitialGalleryRoot() {
			const initialVariantId = this.dataset.initialVariantId
			if (initialVariantId) {
				const variantGallery = this.querySelector(`[data-variant-media-gallery][data-variant-id="${initialVariantId}"]`)
				if (variantGallery) return variantGallery
			}

			return this.querySelector('[data-default-media-gallery]')
		}

		_setActiveGalleryRoot(galleryRoot) {
			if (!galleryRoot) return

			this.querySelectorAll('[data-media-gallery-root]').forEach((root) => {
				const isActive = root === galleryRoot
				root.hidden = !isActive
				root.classList.toggle('l-hidden', !isActive)
			})
		}

		_destroyCarousels() {
			if (this.mainCarouselSwiper) {
				if (this._slideChangeHandler) {
					this.mainCarouselSwiper.off('slideChange', this._slideChangeHandler)
				}
				if (this._slideChangeTransitionEndHandler) {
					this.mainCarouselSwiper.off('slideChangeTransitionEnd', this._slideChangeTransitionEndHandler)
				}
				this.mainCarouselSwiper.destroy(true, true)
				this.mainCarouselSwiper = null
			}

			this.thumbnailCarouselSwiper?.destroy(true, true)
			this.thumbnailCarouselSwiper = null
			this._mainCarouselResizeObserver?.disconnect()
			this._mainCarouselResizeObserver = null
		}

		_activateGallery(galleryRoot) {
			if (!galleryRoot || this._activeGalleryRoot === galleryRoot) return

			this._destroyCarousels()
			this._setActiveGalleryRoot(galleryRoot)
			this._activeGalleryRoot = galleryRoot
			this._setupThumbnailCarousel()
			this._setupMainCarousel()
			this._syncCarousels()
			this._bindSlideChangeEvent()
			this._observeMainCarouselResize()
			this._scheduleUpdateMainMediaHeightVar()
			this._updatePaginationCurrent()
		}

		_onWindowResize() {
			if (this._resizeRaf) cancelAnimationFrame(this._resizeRaf)
			this._resizeRaf = requestAnimationFrame(() => {
				this._resizeRaf = null
				this._updateMainMediaHeightVar()
			})
		}

		_scheduleUpdateMainMediaHeightVar() {
			if (this._mainMediaHeightRaf) cancelAnimationFrame(this._mainMediaHeightRaf)
			this._mainMediaHeightRaf = requestAnimationFrame(() => {
				this._mainMediaHeightRaf = null
				this._updateMainMediaHeightVar()
			})
		}

		_observeMainCarouselResize() {
			this._mainCarouselResizeObserver?.disconnect()
			this._mainCarouselResizeObserver = null
			if (!this.mainCarouselEl || typeof ResizeObserver === 'undefined') return
			this._mainCarouselResizeObserver = new ResizeObserver(() => {
				this._scheduleUpdateMainMediaHeightVar()
			})
			this._mainCarouselResizeObserver.observe(this.mainCarouselEl)
		}

		_updateMainMediaHeightVar() {
			if (!this.mediaSlidesEls?.length) return
			const idx = this.mainCarouselSwiper ? this.mainCarouselSwiper.activeIndex : 0
			const slideEl = this.mediaSlidesEls[idx]
			if (!slideEl) return
			const height = slideEl.getBoundingClientRect().height
			if (height <= 0) return
			this.style.setProperty('--l-main-media-height', `${Math.round(height)}px`)
			// The thumbnail column's max-height follows this variable; recalculate Swiper's
			// size/snap grid so it scrolls within the new bounds instead of stale ones.
			this.thumbnailCarouselSwiper?.update()
		}

		_updatePaginationCurrent() {
			const currentEl = this.mainCarouselEl?.querySelector('[data-current-slide]')
			if (!currentEl || !this.mainCarouselSwiper) return
			const idx =
				typeof this.mainCarouselSwiper.realIndex === 'number'
					? this.mainCarouselSwiper.realIndex
					: this.mainCarouselSwiper.activeIndex
			currentEl.textContent = String(idx + 1)
		}

		_initModalZoomEvents() {
			const zoomMediaBtnEls = this.querySelectorAll('[data-modal-zoom-media]')
			zoomMediaBtnEls.forEach((btn) => {
				btn.addEventListener('click', () => {
					this.dispatchEvent(
						new CustomEvent('l:media:click', {
							bubbles: true,
							detail: { mediaId: btn.dataset.modalZoomMedia },
						})
					)
				})
			})
		}

		setupCarousel() {
			this._activateGallery(this._getInitialGalleryRoot())
		}

		_setupThumbnailCarousel() {
			if (!this._activeGalleryRoot) return

			this.thumbnailCarousel = this._activeGalleryRoot.querySelector('[data-thumbnails-carousel]')
			if (!this.thumbnailCarousel) return

			this.thumbnailSlides = this.thumbnailCarousel.querySelectorAll('[data-media]')
			this.thumbnailSlides.forEach((slide) => slide.classList.remove('l-hidden'))

			this.thumbnailCarouselSwiper = new window.Swiper(this.thumbnailCarousel, {
				loop: false,
				slidesPerView: 'auto',
				spaceBetween: 8,
				resistanceRatio: 0.2,
				direction: 'horizontal',
				// Let the thumbnail strip scroll freely so thumbnails that were pushed out of
				// the clipped column (e.g. after clicking a lower thumbnail) can be reached again.
				watchSlidesProgress: true,
				freeMode: { enabled: true, sticky: false, momentumBounce: false },
				mousewheel: { enabled: true, forceToAxis: true, releaseOnEdges: true },
				breakpoints: {
					1280: {
						direction: 'vertical',
					},
				},
				navigation: {
					nextEl: this.thumbnailCarousel.querySelector('[data-next]'),
					prevEl: this.thumbnailCarousel.querySelector('[data-prev]'),
				},
			})
		}

		_setupMainCarousel() {
			if (!this._activeGalleryRoot) return

			this.mainCarouselEl = this._activeGalleryRoot.querySelector('[data-main-carousel]')
			if (!this.mainCarouselEl) return

			this.mediaSlidesEls = this.mainCarouselEl.querySelectorAll('[data-media]')
			this.navigationNextEl = this.mainCarouselEl.querySelector('[data-next]')
			this.navigationPrevEl = this.mainCarouselEl.querySelector('[data-prev]')

			this.mediaSlidesEls.forEach((slide) => slide.classList.remove('l-hidden'))

			this.mainCarouselSwiper = new window.Swiper(this.mainCarouselEl, {
				loop: false,
				slidesPerView: 'auto',
				spaceBetween: 0,
				autoHeight: true,
				keyboard: { enabled: true },
				navigation: {
					nextEl: this.navigationNextEl,
					prevEl: this.navigationPrevEl,
				},
				thumbs: {
					swiper: this.thumbnailCarouselSwiper,
					// Keep one neighbouring thumbnail in view when auto-scrolling to the active one
					autoScrollOffset: 1,
				},
			})
		}

		_syncCarousels() {
			if (this.mainCarouselSwiper && this.thumbnailCarouselSwiper) {
				this.mainCarouselSwiper.controller.control = this.thumbnailCarouselSwiper
			}
		}

		_bindSlideChangeEvent() {
			if (!this.mainCarouselSwiper) return

			this._slideChangeHandler = (swiper) => {
				const activeSlideEl = this.mediaSlidesEls[swiper.activeIndex]

				l_pauseAllMedia()
				l_playMedia(activeSlideEl)
				this._updatePaginationCurrent()
				this._scheduleUpdateMainMediaHeightVar()
			}

			this._slideChangeTransitionEndHandler = () => {
				this._updatePaginationCurrent()
				this._scheduleUpdateMainMediaHeightVar()
			}

			this.mainCarouselSwiper.on('slideChange', this._slideChangeHandler)
			this.mainCarouselSwiper.on('slideChangeTransitionEnd', this._slideChangeTransitionEndHandler)
		}

		onVariantChange(event) {
			const { sectionId, productId, variant } = event.detail || {}
			if (productId !== this.productId || sectionId !== this.sectionId || !variant) return

			const galleryRoot = this._getGalleryRootForVariant(variant)
			this._activateGallery(galleryRoot)

			const featuredMediaId = variant?.featured_media?.id
			if (!featuredMediaId || galleryRoot?.hasAttribute('data-variant-media-gallery')) {
				this.mainCarouselSwiper?.slideTo(0, true)
				return
			}

			const activeSlideIndex = Array.from(this.mediaSlidesEls).findIndex(
				(mediaSlideEl) => mediaSlideEl.dataset.mediaId == `${featuredMediaId}`
			)

			this.mainCarouselSwiper?.slideTo(activeSlideIndex === -1 ? 0 : activeSlideIndex, true)
		}
	}

	customElements.define('l-product-media-gallery', ProductMediaGallery)
}

/*****************************************
 * [COMPONENT]: EXTERNAL MEDIA
 ******************************************/

class LTAProductModalExternalMedia extends HTMLElement {
	constructor() {
		super()

		this.templateEl = null
		this.templateContentEl = null
	}

	connectedCallback() {
		this.templateEl = this.querySelector('template')
	}

	playVideo() {
		if (!this.templateContentEl && this.templateEl) {
			this.templateContentEl = this.templateEl.content.cloneNode(true)
		}

		if (this.templateContentEl) {
			this.appendChild(this.templateContentEl)
		}
	}

	pauseVideo() {
		// this.innerHTML = ''
		// this.templateContentEl = null // Reset the content so video can be played again
		// l_pauseAllMedia(this)
	}
}

customElements.define('l-external-media', LTAProductModalExternalMedia)

/************************************************************************************
 *
 * [SECTIONS]: PRODUCT ADDITIONAL COMPONENTS
 *
 *************************************************************************************/

/**********************************
 * [PRODUCT BLOCK]: SUBSCRIPTIONS
 ***********************************/

if (!customElements.get('l-subscriptions-selling-plans')) {
	class LTASubscriptionSellingPlans extends HTMLElement {
		constructor() {
			super()
		}

		connectedCallback() {
			this.init()

			document.addEventListener('l:variant:changed', this.handleOnVariantChange.bind(this))
			document.addEventListener('l:quantity:changed', this.handleOnQuantityChange.bind(this))
			this.addEventListener('change', this.handleOnChange.bind(this))
		}

		init() {
			// Elements
			this.saveLabelEl = this.querySelector('[data-save-label]')
			this.variantPriceEl = this.querySelector('[data-variant-price]')
			this.sellingPriceEl = this.querySelector('[data-selling-price]')
			this.sellingComparePriceEl = this.querySelector('[data-selling-compare-price]')
			this.deliverySelectEl = this.querySelector('[data-delivery-select]')

			// Data
			this.productId = this.dataset.productId
			this.sectionId = this.dataset.sectionId

			const script = this.querySelector('script[type="application/json"]')
			this.subscriptionData = script ? JSON.parse(script.textContent) : {}

			this.selectedVariantId = this.subscriptionData?.id
			this.syncDeliverySelectState()
			this.updateSelectedSellingPlanId()
		}

		isSubscriptionSelected() {
			const checkedPlanType = this.querySelector('[data-plan-type-radio]:checked')

			if (!checkedPlanType) return true

			return checkedPlanType.value === 'subscription'
		}

		syncDeliverySelectState() {
			if (!this.deliverySelectEl) return

			// CS 2026-10-28: Shop Pay / express checkout read the plan dropdown even while it is disabled, so a
			// one-time choice could check out as "every 30 days". When one-time is selected the dropdown is fully
			// detached from the form (no name, no form link); it is re-attached only when Subscribe is chosen.
			const sel = this.deliverySelectEl
			if (!sel.dataset.csForm) sel.dataset.csForm = sel.getAttribute('form') || ''
			if (this.isSubscriptionSelected()) {
				sel.removeAttribute('disabled')
				sel.setAttribute('name', 'selling_plan')
				if (sel.dataset.csForm) sel.setAttribute('form', sel.dataset.csForm)
			} else {
				sel.setAttribute('disabled', 'disabled')
				sel.removeAttribute('name')
				sel.removeAttribute('form')
			}
		}

		updateSelectedSellingPlanId() {
			if (this.isSubscriptionSelected()) {
				this.selectedSellingPlanId = this.deliverySelectEl?.value || ''
			} else {
				this.selectedSellingPlanId = ''
			}
		}

		handleOnQuantityChange(event) {
			const { sectionId, productId, quantity } = event.detail

			if (productId !== this.productId || sectionId !== this.sectionId) return

			this.subscriptionData = {
				...this.subscriptionData,
				quantity,
			}

			const { variantPrice, sellingPlanPrice, sellingPlanCompareAtPrice, priceAdjustmentType, saveLabel } =
				this.subscriptionData

			const qtyVariantPrice = l_formatMoney(variantPrice * quantity)
			const qtySellingPlanPrice = l_formatMoney(sellingPlanPrice * quantity)
			const qtySellingPlanCompareAtPrice = l_formatMoney(sellingPlanCompareAtPrice * quantity)

			if (this.variantPriceEl) this.variantPriceEl.innerHTML = qtyVariantPrice
			if (this.sellingPriceEl) this.sellingPriceEl.innerHTML = qtySellingPlanPrice
			if (this.sellingComparePriceEl) this.sellingComparePriceEl.innerHTML = qtySellingPlanCompareAtPrice

			if (priceAdjustmentType == 'price' && this.saveLabelEl) {
				const reducedPrice = l_formatMoney((sellingPlanCompareAtPrice - sellingPlanPrice) * quantity)
				const reducedPercentage =
					sellingPlanCompareAtPrice > 0
						? Math.round(((sellingPlanCompareAtPrice - sellingPlanPrice) / sellingPlanCompareAtPrice) * 100)
						: 0

				this.saveLabelEl.innerHTML = saveLabel
					.replace('[save_amount]', reducedPrice)
					.replace('[save_percentage]', `${reducedPercentage}%`)
			}
		}

		handleOnVariantChange(event) {
			const { sectionId, html, productId, variant } = event.detail

			if (productId !== this.productId || sectionId !== this.sectionId) return

			this.classList.toggle('l-hidden', !variant)
			if (!variant) return

			const newSubscriptionsEl = html.getElementById(`Subscriptions-${this.sectionId}`)
			if (newSubscriptionsEl) {
				this.innerHTML = newSubscriptionsEl.innerHTML
				this.init()
			} else {
				this.classList.add('l-hidden')
			}
		}

		handleOnChange(event) {
			const target = event.target

			if (target?.matches('[data-plan-type-radio]')) {
				this.syncDeliverySelectState()
				this.updateSelectedSellingPlanId()
				this.dispatchSellingPlanIdChanged()
				return
			}

			if (target?.matches('[data-delivery-select]')) {
				this.selectedSellingPlanId = target.value
				this.dispatchSellingPlanIdChanged()
			}
		}

		dispatchSellingPlanIdChanged() {
			this.dispatchEvent(
				new CustomEvent('l:sellingPlan:changed', {
					bubbles: true,
					detail: {
						sellingPlanId: this.selectedSellingPlanId,
						productId: this.productId,
						sectionId: this.sectionId,
						selectedVariantId: this.selectedVariantId,
						target: this,
					},
				})
			)
		}
	}

	customElements.define('l-subscriptions-selling-plans', LTASubscriptionSellingPlans)
}

